import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	ElementRef,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	SimpleChanges,
	ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { trigger, state, style, transition, animate } from '@angular/animations';

import { School } from '../dropdown-school/org.school';
import { OrgService } from '../_services/org.service';
import { ReportService } from '../_services/report-service';
import { PhaseService } from '../_services/phase.service';
import { LoaderService } from '../loader/loader.service';
import { phases, Phase, topData } from './phase.model';
import { ScrollService } from 'src/app/_services/scroll.service';

@Component({
	selector: 'app-reading-achievement-phases',
	templateUrl: './reading-achievement-phases.component.html',
	styleUrls: ['./reading-achievement-phases.component.css'],
	animations: [
		trigger('valueChange', [
			transition('* => *', [style({ opacity: 0 }), animate('300ms ease-in-out', style({ opacity: 1 }))]),
		]),
	],
})
export class ReadingAchievementPhasesComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
	@ViewChild('growthChart', { static: true }) growthChart: ElementRef;
	@Input() node?: any;

	public selectedPhase: any;
	public data: any;
	public topData: any[] = topData;
	public phases: Phase[] = phases;

	subscriptions: Subscription[] = [];
	private currentDataSubscription: Subscription | null = null;
	private observer: IntersectionObserver;

	completed: { baseline: { percentage: number; raw: number }; current: { percentage: number; raw: number } } = {
		baseline: { percentage: 0, raw: 0 },
		current: { percentage: 0, raw: 0 },
	};
	totalCountedStudents: any;
	isScrolling: boolean;
	showRaw: boolean = false; // default to showing percentages
	animateKey: number = 0;

	// New properties for dynamic calculations
	public includeNotAssessed: boolean = false; // Toggle for including participantsWithNoScores
	public calculatedData: any = null; // Store calculated data
	public showPercentages: boolean = true; // Toggle between displaying percentages (true) or raw numbers (false)
	private originalData: any = null; // Store original unmodified data from backend
	public lastPulledTimestamp: number = Date.now(); // Track when data was last fetched
	/** Cached participant table rows with baseline and current (updated when data loads). */
	public participantTableData: {
		name: string;
		classroom: string;
		assessedAtBaseline: boolean;
		baselinePlacement: string;
		baselineDivision: string;
		baselineNotAssessed: boolean;
		currentPlacement: string;
		currentDivision: string;
		currentNotAssessed: boolean;
		lastTestDate: string;
		timesAssessed: number;
	}[] = [];
	/** Admin-only participant table: collapsed by default. */
	public participantTableExpanded = false;
	/** Participant table: search and column filters. */
	public participantTableSearch = '';
	public participantTableFilterClassroom = '';
	public participantTableFilterBaselinePhase = '';
	public participantTableFilterBaselineDivision = '';
	public participantTableFilterCurrentPhase = '';
	public participantTableFilterCurrentDivision = '';

	constructor(
		private orgService: OrgService,
		private reportService: ReportService,
		private phaseService: PhaseService,
		public LoaderService: LoaderService,
		public ScrollService: ScrollService,
		private cdr: ChangeDetectorRef
	) {}

	ngOnInit(): void {
		this.start();
		this.setupIntersectionObserver();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.node) {
			// Cancel any ongoing request when node changes
			if (this.currentDataSubscription) {
				this.currentDataSubscription.unsubscribe();
				this.currentDataSubscription = null;
			}
			// Reset all values to 0 when node changes
			this.resetAllValues();
			// If node is null, clear data and stop loading
			if (!changes.node.currentValue) {
				this.data = null;
				this.LoaderService.setLoadingGrowthChart(false);
				return;
			}
			this.loadDataForNode(changes.node.currentValue);
		}
	}

	ngAfterViewInit(): void {
		this.phases.forEach((phase) => {
			const element = document.getElementById(`phase-${phase.id}`);
			if (element) {
				this.observer.observe(element);
			}
		});
	}

	setupIntersectionObserver(): void {
		this.observer = new IntersectionObserver(
			(entries) => {
				if (this.isScrolling) {
					return;
				}
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						console.log('Growth Chart tab is in view');
						const phaseIds = [1011, 1012, 1013];
						phaseIds.forEach((phaseId) => {
							if (entry.target.id === `phase-${phaseId}`) {
								console.log('Phase', phaseId, 'is in view');
								this.setPhase(phaseId);
							}
						});
					}
				});
			},
			{ threshold: 0.1 }
		);
	}

	ngOnDestroy(): void {
		this.subscriptions.forEach((sub) => sub.unsubscribe());
		if (this.currentDataSubscription) {
			this.currentDataSubscription.unsubscribe();
			this.currentDataSubscription = null;
		}
		this.observer.disconnect();
	}
	toggleDataDisplay(): void {
		this.showRaw = !this.showRaw;
		this.animateKey++;
		// Recalculate topData when toggling
		if (this.data) {
			this.toggleDisplayMode();
		}
	}

	start(): void {
		// If node is provided, use it; otherwise subscribe to selectedSchool$

		if (this.node) {
			this.loadDataForNode(this.node);
		} else {
			this.subscriptions.push(
				this.orgService.selectedSchool$.subscribe((data: School) => {
					this.loadDataForNode(data);
				})
			);
		}

		this.subscriptions.push(
			this.phaseService.selectedPhase$.subscribe((phase: any) => {
				this.selectedPhase = phase;
				this.scrollTo(phase.assessmentBookId);
			})
		);
	}

	private resetAllValues(): void {
		// Reset completed values
		this.completed.baseline.percentage = 0;
		this.completed.baseline.raw = 0;
		this.completed.current.percentage = 0;
		this.completed.current.raw = 0;

		// Reset other values
		this.totalCountedStudents = 0;
		this.data = null;
		this.calculatedData = null;
		this.originalData = null;
		this.participantTableData = [];
		this.participantTableExpanded = false;
		this.lastPulledTimestamp = Date.now();

		// Reset topData to all zeros with proper structure
		this.topData = this.phases.map((phase) => ({
			id: phase.id,
			label: phase.title,
			title: phase.short,
			book: phase,
			color: '#f9f9f9',
			image: '/assets/img/balloon_' + phase.id + '.png',
			baseline: 0,
			current: 0,
		}));
	}

	private loadDataForNode(node: any): void {
		if (!node || !node.id) {
			return;
		}

		// Cancel any ongoing request before starting a new one
		if (this.currentDataSubscription) {
			this.currentDataSubscription.unsubscribe();
			this.currentDataSubscription = null;
		}

		// Check for cached timestamp before making request
		this.lastPulledTimestamp = this.reportService.getLastPulledTimestamp(node.id);

		this.currentDataSubscription = this.reportService.getReadingAchievementPhases(node.id).subscribe({
			next: (rawData) => {
				// Store original data (deep copy to preserve it)
				this.originalData = JSON.parse(JSON.stringify(rawData));

				// Calculate percentages from raw data (default: exclude participantsWithNoScores)
				this.calculatedData = this.calculatePercentages(rawData, this.includeNotAssessed);

				// Update data with calculated values so template can access them
				this.data = this.calculatedData;

				// Set completed values with both percentage and raw from frontend calculations
				if (this.calculatedData?.phases?.complete) {
					this.completed.baseline.percentage = this.calculatedData.phases.complete.baseline?.percentage || 0;
					this.completed.current.percentage = this.calculatedData.phases.complete.current?.percentage || 0;
					this.completed.baseline.raw = this.getCompleteRawCount('baseline');
					this.completed.current.raw = this.getCompleteRawCount('current');
				}

				this.totalCountedStudents =
					rawData.phases?.[1011]?.current?.totalStudents || this.calculatedData?.phases?.[1011]?.current?.totalStudents;

				// Update last pulled timestamp (refresh in case it was just updated)
				this.lastPulledTimestamp = this.reportService.getLastPulledTimestamp(node.id);

				this.updateTopData();
				this.participantTableData = this.getParticipantTableData();
				this.LoaderService.setLoadingGrowthChart(false);
				this.animateKey++; // Trigger animation when new data arrives
				this.currentDataSubscription = null; // Clear reference after completion
			},
			error: (error) => {
				// Handle error and stop loading
				this.LoaderService.setLoadingGrowthChart(false);
				this.currentDataSubscription = null;
			},
		});
	}

	calculatePercentage(data, phase, type) {
		const rawSum =
			data.phases[phase][type]['divisions']['raw'][0] +
			data.phases[phase][type]['divisions']['raw'][1] +
			data.phases[phase][type]['divisions']['raw'][2] +
			data.phases[phase][type]['divisions']['raw'][3] +
			data.phases[phase][type]['divisions']['raw'][4];

		if (this.showRaw) {
			// Return raw sum
			return rawSum;
		} else {
			// Return percentage
			return this.ifOneHundredRound(((rawSum / this.totalCountedStudents) * 100).toFixed(2));
		}
	}

	ifOneHundredRound(twoDecimalPointNumber) {
		if (twoDecimalPointNumber % 1 == 0) {
			return Math.round(twoDecimalPointNumber);
		} else {
			return twoDecimalPointNumber;
		}
	}

	scrollTo(book) {
		this.isScrolling = true;
		setTimeout(() => {
			this.isScrolling = false;
		}, 400);

		this.ScrollService.scrollTo('#phase-' + book);
	}

	setPhase(book) {
		switch (book) {
			case 1011:
				this.selectedPhase = {
					assessmentBookId: 1011,
					title: 'Phase I - Becoming a Reader',
				};
				break;
			case 1012:
				this.selectedPhase = {
					assessmentBookId: 1012,
					title: 'Phase II - Becoming a Skilled Reader',
				};
				break;
			case 1013:
				this.selectedPhase = {
					assessmentBookId: 1013,
					title: 'Phase III - Becoming an Expert Reader',
				};
				break;
		}
		this.cdr.detectChanges();
		this.phaseService.setPhase(this.selectedPhase);
	}

	// Calculate all percentages from raw data, optionally including participantsWithNoScores
	calculatePercentages(data: any, includeNotAssessed: boolean = false): any {
		if (!data || !data.phases) {
			return data;
		}

		const modes: ('baseline' | 'current')[] = ['baseline', 'current'];
		// Deep copy phases to avoid modifying original data
		const calculatedPhases = JSON.parse(JSON.stringify(data.phases));

		modes.forEach((mode: 'baseline' | 'current') => {
			if (includeNotAssessed && data.notAssessedCounts?.[mode]?.['students']) {
				// students is an object, not an array, so use Object.keys().length
				const students = data.notAssessedCounts[mode]['students'];
				const notAssessedCount = Object.keys(students).length;
				if (notAssessedCount > 0) {
					const divisionsData = calculatedPhases[1011][mode].divisions.data;
					const lastIndex = divisionsData.length - 1; //emerging data

					// Read original value directly from source data and add notAssessedCount
					const originalValue = data.phases[1011][mode].divisions.data[lastIndex] || 0;
					calculatedPhases[1011][mode].divisions.data[lastIndex] = originalValue + notAssessedCount; //add notAssessedCount to the last index
				}
			}

			// Step 2: Calculate total participants across all phases + complete
			// Now division[0] of phase 1011 already includes notAssessed if applicable
			const totalParticipantsAcrossAllPhases = this.calculateTotalStudents(calculatedPhases, mode);

			// Step 3: Calculate percentages for each phase

			Object.keys(calculatedPhases).forEach((phaseKey) => {
				if (phaseKey !== 'complete' && phaseKey !== '_meta') {
					const phase = calculatedPhases[phaseKey];
					if (phase[mode]?.divisions?.data) {
						// Initialize percentage array if it doesn't exist
						if (!phase[mode].divisions.percentage) {
							phase[mode].divisions.percentage = [];
						}

						// Initialize raw array if it doesn't exist
						if (!phase[mode].divisions.raw) {
							phase[mode].divisions.raw = [];
						}

						// Calculate percentage for each division and ensure raw array matches data
						phase[mode].divisions.data.forEach((rawValue: number, index: number) => {
							// Update raw array to match data (important for when notAssessed is added)
							phase[mode].divisions.raw[index] = rawValue;

							// Calculate percentage
							phase[mode].divisions.percentage[index] =
								totalParticipantsAcrossAllPhases > 0
									? this.roundToTwoDecimals((rawValue / totalParticipantsAcrossAllPhases) * 100)
									: 0;
						});

						// Calculate totalRaw (sum of divisions)
						phase[mode].totalRaw = phase[mode].divisions.data.reduce((sum: number, val: number) => sum + (val || 0), 0);

						// Calculate totalPercent directly from raw data (not from summing rounded division percentages)
						// This avoids rounding errors that occur when summing already-rounded values
						phase[mode].totalPercent =
							totalParticipantsAcrossAllPhases > 0
								? this.roundToTwoDecimals((phase[mode].totalRaw / totalParticipantsAcrossAllPhases) * 100)
								: 0;
					}
				}
			});

			// Step 3: Calculate complete phase percentage
			if (calculatedPhases['complete']?.[mode]) {
				// students is an object (not array), so use Object.keys().length
				const completeCount = calculatedPhases['complete'][mode].students
					? Object.keys(calculatedPhases['complete'][mode].students).length
					: calculatedPhases['complete'][mode].count || 0;
				calculatedPhases['complete'][mode].percentage =
					totalParticipantsAcrossAllPhases > 0
						? this.roundToTwoDecimals((completeCount / totalParticipantsAcrossAllPhases) * 100)
						: 0;
			}

			// Step 4: Calculate "not assessed" percentage if including them
			if (includeNotAssessed && data.notAssessedCounts?.[mode]?.['students']) {
				// students is an object, not an array, so use Object.keys().length
				const students = data.notAssessedCounts[mode]['students'];
				const notAssessedCount = Object.keys(students).length;
				if (notAssessedCount > 0) {
					const notAssessedPercent =
						totalParticipantsAcrossAllPhases > 0
							? this.roundToTwoDecimals((notAssessedCount / totalParticipantsAcrossAllPhases) * 100)
							: 0;

					// Store in a convenient location for display
					if (!calculatedPhases['_notAssessed']) {
						calculatedPhases['_notAssessed'] = {};
					}
					if (!calculatedPhases['_notAssessed'][mode]) {
						calculatedPhases['_notAssessed'][mode] = {};
					}

					calculatedPhases['_notAssessed'][mode].count = notAssessedCount;
					calculatedPhases['_notAssessed'][mode].percentage = notAssessedPercent;
				}
			}
		});

		return {
			...data,
			phases: calculatedPhases,
		};
	}

	// Toggle "not assessed" (participantsWithNoScores) inclusion
	toggleNotAssessed(): void {
		this.includeNotAssessed = !this.includeNotAssessed;
		this.recalculateData();
	}

	// Toggle between showing percentages and raw numbers
	toggleDisplayMode(): void {
		this.showPercentages = !this.showPercentages;
		this.updateTopData();
		// Update completed values based on display mode
		// Use phases.complete for recalculated percentages (includes notAssessed toggle)
		if (this.calculatedData?.phases?.complete) {
			this.completed.baseline.percentage = this.calculatedData.phases.complete.baseline?.percentage || 0;
			this.completed.current.percentage = this.calculatedData.phases.complete.current?.percentage || 0;
			this.completed.baseline.raw = this.getCompleteRawCount('baseline');
			this.completed.current.raw = this.getCompleteRawCount('current');
		}
		this.cdr.detectChanges(); // Ensure template updates
	}

	// Get display value (percentage or raw) based on showPercentages flag
	getDisplayValue(phaseId: number, mode: string): number {
		if (!this.calculatedData || !this.calculatedData.phases || !this.calculatedData.phases[phaseId]) {
			return 0;
		}

		if (this.showPercentages) {
			return this.calculatedData.phases[phaseId][mode]?.totalPercent || 0;
		} else {
			return this.calculatedData.phases[phaseId][mode]?.totalRaw || 0;
		}
	}

	// Get division display value (percentage or raw) based on showPercentages flag
	getDivisionDisplayValue(phaseId: number, mode: string, divisionIndex: number): number {
		if (!this.calculatedData || !this.calculatedData.phases || !this.calculatedData.phases[phaseId]) {
			return 0;
		}

		if (this.showPercentages) {
			return this.calculatedData.phases[phaseId][mode]?.divisions?.percentage?.[divisionIndex] || 0;
		} else {
			return (
				this.calculatedData.phases[phaseId][mode]?.divisions?.raw?.[divisionIndex] ||
				this.calculatedData.phases[phaseId][mode]?.divisions?.data?.[divisionIndex] ||
				0
			);
		}
	}

	// Recalculate all data when toggling options
	recalculateData(): void {
		// Use originalData if available, otherwise fall back to data
		const sourceData = this.originalData || this.data;
		if (sourceData) {
			this.calculatedData = this.calculatePercentages(sourceData, this.includeNotAssessed);
			// Update data with calculated values so template can access them
			this.data = this.calculatedData;
			// Update topData with new values
			this.updateTopData();
			// Use phases.complete for recalculated percentages (includes notAssessed toggle)
			if (this.calculatedData?.phases?.complete) {
				this.completed.baseline.percentage = this.calculatedData.phases.complete.baseline?.percentage || 0;
				this.completed.current.percentage = this.calculatedData.phases.complete.current?.percentage || 0;
				this.completed.baseline.raw = this.getCompleteRawCount('baseline');
				this.completed.current.raw = this.getCompleteRawCount('current');
			}
		}
	}

	// Update topData with calculated values (respects display mode toggle)
	updateTopData(): void {
		if (this.calculatedData && this.calculatedData.phases) {
			this.topData = this.phases.map((phase) => ({
				id: phase.id,
				label: phase.title,
				title: phase.short,
				book: phase,
				color: '#f9f9f9',
				image: '/assets/img/balloon_' + phase.id + '.png',
				baseline: this.getDisplayValue(phase.id, 'baseline'),
				current: this.getDisplayValue(phase.id, 'current'),
			}));
			this.animateKey++;
		} else if (this.data) {
			// Fallback to calculatePercentage if calculatedData is not available
			this.topData = this.phases.map((phase) => ({
				id: phase.id,
				label: phase.title,
				title: phase.short,
				book: phase,
				color: '#f9f9f9',
				image: '/assets/img/balloon_' + phase.id + '.png',
				baseline: this.calculatePercentage(this.data, phase.id, 'baseline'),
				current: this.calculatePercentage(this.data, phase.id, 'current'),
			}));
			this.animateKey++;
		}
	}

	// Helper method to get raw count for complete phase
	getCompleteRawCount(mode: string): number {
		if (!this.calculatedData?.phases?.complete?.[mode]) {
			return 0;
		}
		const completePhase = this.calculatedData.phases.complete[mode];
		// Check if students is an object (not array), so use Object.keys().length
		if (completePhase.students) {
			return Object.keys(completePhase.students).length;
		}
		// Otherwise use count
		return completePhase.count || 0;
	}

	// Helper method to get not assessed students count
	getNotAssessedCount(mode: string = 'current'): number {
		if (!this.data?.notAssessedCounts?.[mode]?.['students']) {
			return 0;
		}
		const students = this.data.notAssessedCounts[mode]['students'];
		// students is an object, not an array, so use Object.keys().length
		return Object.keys(students).length;
	}

	// Private helper method to calculate total students from phases data
	private calculateTotalStudents(phases: any, mode: 'baseline' | 'current'): number {
		if (!phases) {
			return 0;
		}

		let totalStudents = 0;

		// Sum all phase divisions (1011, 1012, 1013) - excludes complete, _meta, and _notAssessed
		Object.keys(phases).forEach((phaseKey) => {
			if (phaseKey !== 'complete' && phaseKey !== '_meta' && phaseKey !== '_notAssessed') {
				const phase = phases[phaseKey];
				if (phase[mode]?.divisions?.data) {
					const sumOfDivisions = phase[mode].divisions.data.reduce((sum: number, val: number) => sum + (val || 0), 0);
					totalStudents += sumOfDivisions;
				}
			}
		});

		// Add complete phase count
		if (phases['complete']?.[mode]) {
			const completePhase = phases['complete'][mode];
			if (completePhase.students) {
				// students is an object (not array), so use Object.keys().length
				totalStudents += Object.keys(completePhase.students).length || 0;
			} else if (completePhase.count) {
				totalStudents += completePhase.count || 0;
			}
		}

		return totalStudents;
	}

	// Helper function to get totalStudents for baseline or current
	getTotalStudents(mode: 'baseline' | 'current' = 'current'): number {
		if (!this.calculatedData || !this.calculatedData.phases) {
			return 0;
		}

		return this.calculateTotalStudents(this.calculatedData.phases, mode);
	}

	// Helper method to round to 2 decimal places
	roundToTwoDecimals(value: number, decimals: number = 2): number {
		const factor = Math.pow(10, decimals);
		return Math.round(value * factor) / factor;
	}

	// Format last pulled timestamp for display (uses service method)
	getLastPulledFormatted(): string {
		if (this.node?.id) {
			return this.reportService.getLastPulledFormatted(this.node.id);
		}
	}

	toggleParticipantTable(): void {
		this.participantTableExpanded = !this.participantTableExpanded;
	}

	// Clear cache and reload data
	clearCacheAndReload(): void {
		// Use shared service method to get node and clear cache
		const nodeToReload = this.reportService.clearCacheAndGetNode(this.node);

		if (nodeToReload) {
			// Reload data
			this.loadDataForNode(nodeToReload);
		}
	}

	/** Phase options for Baseline/Current placement dropdowns. */
	get participantTablePhaseOptions(): string[] {
		return ['Phase I', 'Phase II', 'Phase III', 'Complete', 'Not assessed'];
	}
	/** Division options (Emerging, Bronze, etc.) for Baseline/Current dropdowns. */
	get participantTableDivisionOptions(): string[] {
		return ['Emerging', 'Bronze', 'Silver', 'Gold', 'Platinum'];
	}

	/** Unique classrooms from participant table data, for filter dropdown. */
	get participantTableClassrooms(): string[] {
		const set = new Set(this.participantTableData.map((r) => r.classroom).filter(Boolean));
		return Array.from(set).sort();
	}

	/** Participant table rows filtered by search text and column dropdowns. */
	get filteredParticipantTableData(): typeof this.participantTableData {
		const q = (this.participantTableSearch || '').trim().toLowerCase();
		const matchClassroom = (r: (typeof this.participantTableData)[0]) =>
			!this.participantTableFilterClassroom || r.classroom === this.participantTableFilterClassroom;
		const matchBaselinePhase = (r: (typeof this.participantTableData)[0]) => {
			if (!this.participantTableFilterBaselinePhase) return true;
			const p = r.baselinePlacement || '';
			if (this.participantTableFilterBaselinePhase === 'Not assessed') return p === 'Not assessed';
			if (this.participantTableFilterBaselinePhase === 'Complete') return p === 'Complete';
			return p.startsWith(this.participantTableFilterBaselinePhase);
		};
		const matchBaselineDivision = (r: (typeof this.participantTableData)[0]) =>
			!this.participantTableFilterBaselineDivision ||
			r.baselineDivision === this.participantTableFilterBaselineDivision;
		const matchCurrentPhase = (r: (typeof this.participantTableData)[0]) => {
			if (!this.participantTableFilterCurrentPhase) return true;
			const p = r.currentPlacement || '';
			if (this.participantTableFilterCurrentPhase === 'Not assessed') return p === 'Not assessed';
			if (this.participantTableFilterCurrentPhase === 'Complete') return p === 'Complete';
			return p.startsWith(this.participantTableFilterCurrentPhase);
		};
		const matchCurrentDivision = (r: (typeof this.participantTableData)[0]) =>
			!this.participantTableFilterCurrentDivision || r.currentDivision === this.participantTableFilterCurrentDivision;
		const matchSearch = (r: (typeof this.participantTableData)[0]) => {
			if (!q) return true;
			const name = (r.name || '').toLowerCase();
			const classroom = (r.classroom || '').toLowerCase();
			return name.includes(q) || classroom.includes(q);
		};
		return this.participantTableData.filter(
			(r) =>
				matchSearch(r) &&
				matchClassroom(r) &&
				matchBaselinePhase(r) &&
				matchBaselineDivision(r) &&
				matchCurrentPhase(r) &&
				matchCurrentDivision(r)
		);
	}

	/**
	 * Build a flat array of participant data with baseline and current mode, for display in the participant table.
	 * One row per participant; each participant may appear in baseline and/or current across phases 1011, 1012, 1013, complete, and not assessed.
	 */
	getParticipantTableData(): {
		name: string;
		classroom: string;
		assessedAtBaseline: boolean;
		baselinePlacement: string;
		baselineDivision: string;
		baselineNotAssessed: boolean;
		currentPlacement: string;
		currentDivision: string;
		currentNotAssessed: boolean;
		lastTestDate: string;
		timesAssessed: number;
	}[] {
		const source = this.data || this.originalData;
		if (!source?.phases) {
			return [];
		}
		const phaseLabels: Record<string, string> = {
			1011: 'Phase I',
			1012: 'Phase II',
			1013: 'Phase III',
		};

		const getPlacementForStudent = (s: any): string => {
			if (s?.notAssessed) return 'Not assessed';
			if (s?.currentPhase === 1013 && s?.questionId === 100 && s?.score >= 3) return 'Complete';
			const phaseLabel = phaseLabels[String(s?.currentPhase)] ?? '—';
			return `${phaseLabel} - ${s?.division?.value ?? '—'}`;
		};

		const collectByMode = (mode: 'baseline' | 'current'): Record<number, any> => {
			const out: Record<number, any> = {};
			[1011, 1012, 1013].forEach((phaseId) => {
				const students = source.phases[phaseId]?.[mode]?.students;
				if (students && typeof students === 'object') {
					Object.keys(students).forEach((pidStr) => {
						const id = parseInt(pidStr, 10);
						out[id] = students[pidStr];
					});
				}
			});
			const complete = source.phases?.complete?.[mode]?.students;
			if (complete && typeof complete === 'object') {
				Object.keys(complete).forEach((pidStr) => {
					const id = parseInt(pidStr, 10);
					out[id] = complete[pidStr];
				});
			}
			const notAssessed = source.notAssessedCounts?.[mode]?.students;
			if (notAssessed && typeof notAssessed === 'object') {
				Object.keys(notAssessed).forEach((pidStr) => {
					const id = parseInt(pidStr, 10);
					out[id] = notAssessed[pidStr];
				});
			}
			return out;
		};

		const baselineByParticipant = collectByMode('baseline');
		const currentByParticipant = collectByMode('current');
		const allIds = new Set<number>([
			...Object.keys(baselineByParticipant).map((k) => parseInt(k, 10)),
			...Object.keys(currentByParticipant).map((k) => parseInt(k, 10)),
		]);

		const rows: {
			name: string;
			classroom: string;
			assessedAtBaseline: boolean;
			baselinePlacement: string;
			baselineDivision: string;
			baselineNotAssessed: boolean;
			currentPlacement: string;
			currentDivision: string;
			currentNotAssessed: boolean;
			lastTestDate: string;
			timesAssessed: number;
		}[] = [];

		allIds.forEach((id) => {
			const baseline = baselineByParticipant[id];
			const current = currentByParticipant[id];
			const s = current ?? baseline;
			const name = s?.name ?? ([s?.fname, s?.lname].filter(Boolean).join(' ') || '—');
			// Assessed at baseline = has a baseline record with actual assessment data (not in "not assessed" bucket)
			const assessedAtBaseline = !!baseline && !baseline.notAssessed;

			const rawClassroom = s?.classroom;
			let classroom = '—';
			if (rawClassroom != null) {
				if (typeof rawClassroom === 'string') {
					classroom = rawClassroom;
				} else if (typeof rawClassroom === 'object') {
					classroom =
						(rawClassroom as any)?.name ?? (rawClassroom as any)?.entity ?? (rawClassroom as any)?.title ?? '—';
				}
			}

			rows.push({
				name,
				classroom,
				assessedAtBaseline,
				baselinePlacement: baseline ? getPlacementForStudent(baseline) : '—',
				baselineDivision: baseline?.division?.value ?? '—',
				baselineNotAssessed: !!baseline?.notAssessed,
				currentPlacement: current ? getPlacementForStudent(current) : '—',
				currentDivision: current?.division?.value ?? '—',
				currentNotAssessed: !!current?.notAssessed,
				lastTestDate: s?.lastTestDate ?? '—',
				timesAssessed: s?.timesAssessed ?? 0,
			});
		});

		return rows;
	}
}
